import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { gmbApi } from '@/services/gmbApi';
import type { RegenerationNotDueError } from '../types';

function getUserId() {
  return localStorage.getItem('user_id') || '';
}

// ── Strategy Overview ──────────────────────────────────────────────────

export function useGmbStrategyOverview() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'strategy', 'overview', userId],
    queryFn: () => gmbApi.getStrategyOverview(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

// ── As-Is State ────────────────────────────────────────────────────────

export function useGmbAsIs() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'strategy', 'asis', userId],
    queryFn: () => gmbApi.getAsIs(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Goals ──────────────────────────────────────────────────────────────

export function useGmbGoals() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'strategy', 'goals', userId],
    queryFn: () => gmbApi.getGoals(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateGmbGoal() {
  const queryClient = useQueryClient();
  const userId = getUserId();

  return useMutation({
    mutationFn: ({ goalId, data }: { goalId: string; data: { target?: string; status?: string } }) =>
      gmbApi.updateGoal(userId, goalId, data),
    onSuccess: () => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'strategy', 'goals', userId] });
    },
  });
}

// ── Action Plan ────────────────────────────────────────────────────────

export function useGmbTechnicalPlan() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'strategy', 'action-plan', 'technical', userId],
    queryFn: () => gmbApi.getTechnicalPlan(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGmbContentPlan() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'strategy', 'action-plan', 'content', userId],
    queryFn: () => gmbApi.getContentPlan(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGenerateContentPlan() {
  const queryClient = useQueryClient();
  const userId = getUserId();

  return useMutation({
    mutationFn: () => gmbApi.generateContentPlan(userId),
    onSuccess: () => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'strategy', 'action-plan', 'content', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'posts', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'overview', userId] });
    },
  });
}

export function useUpdateContentPostStatus() {
  const queryClient = useQueryClient();
  const userId = getUserId();

  return useMutation({
    mutationFn: ({ postId, status }: { postId: string; status: string }) =>
      gmbApi.updateContentPostStatus(userId, postId, status),
    onSuccess: () => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'strategy', 'action-plan', 'content', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'posts', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'overview', userId] });
    },
  });
}

export function useGmbDescriptionGuide() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'strategy', 'action-plan', 'description-guide', userId],
    queryFn: () => gmbApi.getDescriptionGuide(userId),
    enabled: !!userId,
  });
}

export function useGenerateTaskDetails() {
  const queryClient = useQueryClient();
  const userId = getUserId();
  return useMutation({
    mutationFn: (force: boolean = false) => gmbApi.generateTaskDetails(userId, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmb', 'strategy', 'action-plan', 'technical', userId] });
    },
  });
}

export function useSuggestGmbDescription() {
  const userId = getUserId();
  return useMutation({
    mutationFn: () => gmbApi.suggestDescription(userId),
  });
}

export function useSaveGmbDescription() {
  const queryClient = useQueryClient();
  const userId = getUserId();

  return useMutation({
    mutationFn: (description: string) => gmbApi.saveDescriptionFromGuide(userId, description),
    onSuccess: () => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'strategy', 'action-plan', 'description-guide', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'connection-status', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'strategy', 'asis', userId] });
    },
  });
}

export function useUpdateTaskStatus() {
  const queryClient = useQueryClient();
  const userId = getUserId();
  const queryKey = ['gmb', 'strategy', 'action-plan', 'technical', userId];

  return useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: string }) =>
      gmbApi.updateTaskStatus(userId, taskId, status),
    onSuccess: (response: any) => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      const updated = response?.data;
      if (updated?.id) {
        queryClient.setQueryData(queryKey, (old: any) => {
          if (!old?.data) return old;
          const patch = (tasks: any[]) =>
            tasks.map((t: any) => t.task_id === updated.id ? { ...t, status: updated.status } : t);
          return {
            ...old,
            data: {
              ...old.data,
              checklist: patch(old.data.checklist || []),
              maintenance: patch(old.data.maintenance || []),
            },
          };
        });
      }
      queryClient.invalidateQueries({ queryKey });
      // Implementation Overview's One-time / Weekly cards count these tasks.
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'overview', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'checklist', userId] });
    },
  });
}

export function useGenerateStrategy() {
  const queryClient = useQueryClient();
  const userId = getUserId();

  return useMutation({
    mutationFn: () => gmbApi.generateStrategy(userId),
    onSuccess: () => {
      // Governance reads these changes (activity log, publishing status).
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance'] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'strategy'] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'posts', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'implementation', 'overview', userId] });
    },
    onError: (error: any) => {
      // 403 REGENERATION_NOT_DUE is expected — handled by caller via error.response
    },
  });
}

// ── Visibility ─────────────────────────────────────────────────────────

export function useGmbVisibility() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'visibility', userId],
    queryFn: () => gmbApi.getVisibility(userId),
    enabled: !!userId,
    staleTime: 10 * 60 * 1000,
  });
}

// ── Connection Status ──────────────────────────────────────────────────

export function useGmbConnectionStatus() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'connection-status', userId],
    queryFn: () => gmbApi.getConnectionStatus(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}
